// ResumeUpload.jsx
// PDF upload component for fast profile onboarding
// Supports resume PDFs and LinkedIn "Save to PDF" exports

import React, { useState, useRef } from 'react';

export default function ResumeUpload({ onProfileParsed, onSkip }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }

    setFileName(file.name);
    setError(null);
    setIsUploading(true);

    try {
      // Convert file to base64
      const base64 = await fileToBase64(file);

      // Remove the data URL prefix (keep only the base64 data)
      const base64Data = base64.split(',')[1];

      // Call the API
      const response = await fetch('/api/parse-resume', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pdfBase64: base64Data,
          fileName: file.name,
        }),
      });

      const result = await response.json();

      if (result.success) {
        onProfileParsed(result.profile, result.source);
      } else {
        setError(result.error || 'Failed to parse document');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Upload Area */}
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{
          border: `2px dashed ${isDragging ? '#f7c948' : '#d8d8d8'}`,
          borderRadius: '8px',
          padding: '32px 24px',
          textAlign: 'center',
          cursor: isUploading ? 'wait' : 'pointer',
          background: isDragging ? '#fffef5' : '#fafafa',
          transition: 'all 0.2s ease',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          onChange={handleInputChange}
          style={{ display: 'none' }}
        />

        {isUploading ? (
          <>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style={{ margin: '0 auto 12px', animation: 'spin 1s linear infinite' }}>
              <circle cx="12" cy="12" r="10" stroke="#e8e8e8" strokeWidth="3" />
              <path d="M12 2a10 10 0 0 1 10 10" stroke="#f7c948" strokeWidth="3" strokeLinecap="round" />
            </svg>
            <style>{`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}</style>
            <p style={{ margin: 0, fontSize: '14px', color: '#3d3d3d', fontWeight: '500' }}>
              Reading your {fileName?.toLowerCase().includes('linkedin') ? 'LinkedIn profile' : 'resume'}...
            </p>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#7a7a7a' }}>
              This may take a few seconds
            </p>
          </>
        ) : (
          <>
            {/* Upload Icon */}
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style={{ margin: '0 auto 12px' }}>
              <path d="M12 16V4M12 4L8 8M12 4L16 8" stroke="#7a7a7a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M20 16V18C20 19.1 19.1 20 18 20H6C4.9 20 4 19.1 4 18V16" stroke="#7a7a7a" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <p style={{ margin: 0, fontSize: '14px', color: '#3d3d3d', fontWeight: '500' }}>
              Drop your resume or LinkedIn PDF here
            </p>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#7a7a7a' }}>
              or click to browse
            </p>
          </>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          marginTop: '12px',
          padding: '10px 14px',
          background: '#fce8e8',
          border: '1px solid #d4736a',
          borderRadius: '4px',
          color: '#943c33',
          fontSize: '13px',
        }}>
          {error}
        </div>
      )}

      {/* LinkedIn Instructions */}
      <div style={{
        marginTop: '20px',
        padding: '16px',
        background: '#f8f9fa',
        borderRadius: '6px',
        border: '1px solid #e8e8e8',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          {/* LinkedIn Icon */}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="#0a66c2" style={{ flexShrink: 0, marginTop: '2px' }}>
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
          <div>
            <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#3d3d3d' }}>
              Export your LinkedIn profile as PDF
            </p>
            <ol style={{ margin: '8px 0 0', paddingLeft: '18px', fontSize: '12px', color: '#5a5a5a', lineHeight: '1.6' }}>
              <li>Open your LinkedIn profile on <strong>desktop</strong></li>
              <li>Click the <strong>"More"</strong> button below your profile photo</li>
              <li>Select <strong>"Save to PDF"</strong></li>
              <li>Upload the downloaded PDF here</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Skip Option */}
      {onSkip && (
        <button
          onClick={onSkip}
          style={{
            width: '100%',
            marginTop: '16px',
            padding: '12px',
            background: 'none',
            border: 'none',
            color: '#7a7a7a',
            fontSize: '13px',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Skip - I'll enter my info manually
        </button>
      )}
    </div>
  );
}
